import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isSameDay } from 'date-fns';
import { useAppContext } from 'src/app/state';
import { IoMdClose } from 'react-icons/io';
import Button from 'src/app/components/kit/button/Button';
import Text from 'src/app/components/kit/text/Text';
import DateTimePicker from 'src/app/components/kit/date-time-picker/DateTimePicker';
import { ActionType } from 'src/app/state/action-type.enum';
import { MigraineAttack } from 'src/types/migraine-attack.types';
import styles from './MigraineAttackPage.module.css';
import ActionButton from 'src/app/components/kit/action-button/ActionButton';
import { AppRoutes } from 'src/types/app-routes.enum';

const PosologyButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      type='white'
      width='full-width'
      onClick={onClick}
      style={{ width: '100%', marginLeft: 0 }}
    >
      View Posology
    </Button>
  );
};

const MigraineAttackPage = () => {
  const navigate = useNavigate();
  const {
    state: { migraineAttacks },
    dispatch,
  } = useAppContext();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dateInParams = params.get('day');
  const locationInParams = params.get('location');
  const date = useMemo(
    () => (dateInParams ? new Date(dateInParams) : new Date()),
    [dateInParams]
  );
  const savedMigraineAttack: MigraineAttack | undefined = useMemo(
    () => migraineAttacks.find((attack) => isSameDay(attack.date!, date)),
    [migraineAttacks, date]
  );
  const pastAttackDates: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => !isSameDay(record.date!, date))
        .map((record) => record.date!),
    [date, migraineAttacks]
  );
  const migraineDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.isMigraineDay)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraEveryOtherDayDates: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraEveryOtherDay === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraPerAttackDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraPerAttack === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const [attack, setAttack] = useState(
    savedMigraineAttack
      ? {
          ...savedMigraineAttack,
          isMigraineDay: true,
        }
      : {
          date,
          vyduraPerAttack: false,
          vyduraEveryOtherDay: false,
          isMigraineDay: true,
        }
  );
  const handleWindowClose = () => {
    if (locationInParams) {
      const params = new URLSearchParams();
      params.set(
        'day',
        attack.date ? attack.date.toDateString() : date.toDateString()
      );
      const pathMap = {
        calendar: `${AppRoutes.Calendar}?${params.toString()}`,
        home: AppRoutes.Home,
      };
      navigate(pathMap[locationInParams as keyof typeof pathMap]);
    } else {
      navigate(-1);
    }
  };
  const onSelectAttackDate = (day: Date) => {
    setAttack((prev) => ({
      ...prev,
      date: day,
    }));
  };
  const onSelectMedication = (
    type: 'vyduraPerAttack' | 'vyduraEveryOtherDay'
  ) => {
    setAttack((prev) => ({
      ...prev,
      vyduraEveryOtherDay:
        type === 'vyduraEveryOtherDay' && !prev.vyduraEveryOtherDay
          ? true
          : false,
      vyduraPerAttack:
        type === 'vyduraPerAttack' && !prev.vyduraPerAttack ? true : false,
    }));
  };
  const onSaveAttack = () => {
    const isChangedSavedAttackDate =
      savedMigraineAttack &&
      !isSameDay(savedMigraineAttack.date!, attack.date!);
    let attackPayload = migraineAttacks;
    if (savedMigraineAttack) {
      attackPayload = isChangedSavedAttackDate
        ? [
            ...migraineAttacks.filter(
              (rec) => !isSameDay(rec.date!, savedMigraineAttack.date!)
            ),
            attack,
          ]
        : [
            ...migraineAttacks.filter(
              (rec) => !isSameDay(rec.date!, attack.date!)
            ),
            attack,
          ];
    } else {
      attackPayload = [...migraineAttacks, attack];
    }
    dispatch({
      type: ActionType.SET_MIGRAINE_ATTACK,
      payload: {
        migraineAttacks: attackPayload,
      },
    });
    handleWindowClose();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Button
          type='transparent'
          width='content-width'
          style={{ padding: 0, height: '21.6px' }}
          onClick={handleWindowClose}
        >
          <IoMdClose size={20} />
        </Button>
      </div>
      <div className={styles.content}>
        <div className={styles.date}>
          <Text tag='span' size='m-small' color='gray'>
            {new Intl.DateTimeFormat('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            }).format(attack.date)}
          </Text>
          <DateTimePicker
            showTimePicker={false}
            selectedDate={date}
            handleSelectDateTime={onSelectAttackDate}
            maxDate={new Date()}
            showAs='button'
            btnText='Edit date'
            datesDisabled={pastAttackDates}
            vyduraEveryDayDates={vyduraEveryOtherDayDates}
            vyduraPerAttacksDates={vyduraPerAttackDays}
            migraineDates={migraineDays}
          />
        </div>
        <Text tag='h2' size='m-large' weight='bold'>
          Tell us more about your attack
        </Text>
        <Text color='gray' style={{ opacity: 0.8 }}>
          Select one option and log your information below
        </Text>
        <Text
          tag='h2'
          size='medium'
          weight='bold'
          style={{ marginTop: '32px' }}
        >
          Medications
        </Text>
        <div className={styles.medications}>
          <div
            className={styles.medication}
            onClick={() => onSelectMedication('vyduraPerAttack')}
          >
            <Button
              type={attack.vyduraPerAttack ? 'dark-grey' : 'light-grey'}
              form='rounded'
              width='full-width'
              style={{ height: 80, width: 80, marginBottom: '12px' }}
            >
              <img
                height='48'
                width='48'
                src={
                  attack.vyduraPerAttack
                    ? '../../images/pill-white.png'
                    : '../../images/pill.png'
                }
                alt=''
                style={{ margin: '0 auto' }}
              />
            </Button>
            <Text
              weight='semi-bold'
              tag='span'
              style={{
                fontSize: '14px',
                textAlign: 'center',
                lineHeight: '20px',
              }}
            >
              Vydura
              <br />
              75mg
            </Text>
            <Text
              size='x-small'
              tag='span'
              color='gray'
              style={{ opacity: 0.8 }}
            >
              Per attack
            </Text>
          </div>
          {savedMigraineAttack?.vyduraEveryOtherDay && (
            <div
              className={styles.medication}
              onClick={() => onSelectMedication('vyduraEveryOtherDay')}
            >
              <Button
                type={attack.vyduraEveryOtherDay ? 'dark-grey' : 'light-grey'}
                form='rounded'
                width='full-width'
                style={{ height: 80, width: 80, marginBottom: '12px' }}
              >
                <img
                  height='48'
                  width='48'
                  src={
                    attack.vyduraEveryOtherDay
                      ? '../../images/pill-white.png'
                      : '../../images/pill.png'
                  }
                  alt=''
                  style={{ margin: '0 auto' }}
                />
              </Button>
              <Text
                weight='semi-bold'
                tag='span'
                style={{
                  fontSize: '14px',
                  textAlign: 'center',
                  lineHeight: '20px',
                }}
              >
                Vydura
                <br />
                75mg
              </Text>
              <Text
                size='x-small'
                tag='span'
                color='gray'
                style={{ opacity: 0.8 }}
              >
                Every other day
              </Text>
            </div>
          )}
        </div>
        <ActionButton
          onConfirm={() => undefined}
          MainButton={PosologyButton}
          popUpText={
            <>
              <Text style={{ paddingBottom: '24px' }} color='gray'>
                <Text tag='span' weight='bold' color='black'>
                  Acute treatment of migraine:
                </Text>{' '}
                The recommended dose is 75mg rimegepant, as needed, once daily.
              </Text>
              <Text color='gray'>
                <Text tag='span' weight='bold' color='black'>
                  Prophylaxis of migraine:
                </Text>{' '}
                The recommended dose is 75mg rimegepant every other day.
              </Text>
              <Text style={{ padding: '24px 0' }} color='gray'>
                The maximum dose per day is 75mg rimegepant.
              </Text>
              <Text color='gray'>
                Vydura can be taken with or without meals.
              </Text>
            </>
          }
          popUpHeader='Posology'
          hasFooter={false}
        />
      </div>
      <div className={styles.footer}>
        {Boolean(savedMigraineAttack) ? (
          <Button
            type='primary'
            width='full-width'
            onClick={onSaveAttack}
            style={{ width: '100%', marginLeft: 0 }}
          >
            Save Record
          </Button>
        ) : (
          <Button
            type='primary'
            width='full-width'
            onClick={onSaveAttack}
            style={{ width: '100%', marginLeft: 0 }}
          >
            Add Record
          </Button>
        )}
      </div>
    </div>
  );
};

export default MigraineAttackPage;
